import React, { useCallback, useEffect, useState } from 'react'
import { Collapse } from 'react-bootstrap'
import { useLocation } from 'react-router-dom'
import _ from 'lodash'

import LineChart from '../../linechart'
import PaceLoader from '../../paceloader'
import Heading from '../heading'
import RefreshButton from '../refresh_button'
import AnalyticsFilters from '../analytics_filters'
import ReportTableOrg from './report_org_table'
import ReportTableIndividual from './report_individual_table'

import { ApiHelper } from '../../../../helpers/apihelper'
import * as Constants from '../../../../helpers/constants'
import { buildApiFilters, getMetrics, getTableHeading, isOrgAdminView } from '../helpers'

/**
 * Port of cowebapp analytics/components/team/index.js — Team Member Usage
 * Reports (org owner/admin only; same report/all endpoint as Overview).
 */
const Team = ({ client, viewedUser, memberOptions }) => {

  const location = useLocation()
  const urlParams = new URLSearchParams(location.search)
  const memberId = urlParams.get('member_id') || null

  const [metrics, setMetrics] = useState({
    totalMembers: 0, totalCredits: 0, totalEVCredits: 0, totalEFCredits: 0, totalProspectCredits: 0, totalJsWidgetCredits: 0, totalDPCredits: 0
  })
  const [isPerfSummaryOpen, setIsPerfSummaryOpen] = useState(true)
  const [usageStats, setUsageStats] = useState([])
  const [usageReports, setUsageReports] = useState([])
  const [showTable, setShowTable] = useState('individual')
  const [isStatsLoading, setIsStatsLoading] = useState(false)
  const [graphData, setGraphData] = useState(null)
  const [isGraphDataLoading, setIsGraphDataLoading] = useState(false)
  const [filters, setFiltersState] = useState({})

  const summaryToggle = () => {
    setIsPerfSummaryOpen(!isPerfSummaryOpen)
  }

  const getGraphData = useCallback((data) => {
    let stat = ''
    if (Object.prototype.hasOwnProperty.call(data, 'user_stats_org')) {
      stat = data.user_stats_org[0]
    } else {
      stat = data.user_stats[0]
    }
    let graphData = {
      breakdown: data.breakdown,
      labels: [],
      values: [
        {
          key: 'ev_billable', label: 'Email Verifier', data: [], borderColor: '#3cba9f',
          backgroundColor: '#71d1bd0f', fill: true
        },
        {
          key: 'ef_billable', label: 'Email Finder', data: [], borderColor: '#ffa500',
          backgroundColor: '#ffc04d0f', fill: true
        },
        {
          key: 'prospect_billable', label: 'Prospect', data: [], borderColor: '#c45850',
          backgroundColor: '#d78f8914', fill: true
        },
        {
          key: 'js_widget_billable', label: 'Form Guard', data: [], borderColor: '#0000FF',
          backgroundColor: '#0000FF14', fill: true
        },
        {
          key: 'data_pulse_billable', label: 'Data Pulse', data: [], borderColor: '#9B59B6',
          backgroundColor: '#9B59B614', fill: true
        }
      ],
    }

    stat.stats.forEach((data) => {
      graphData.labels.push(data.date)
    })

    graphData.values.forEach((item) => {
      stat.stats.forEach((data) => {
        Object.keys(data).forEach((key) => {
          if (key === item.key) {
            item.data.push(data[key])
          }
        })
      })
    })
    return graphData
  }, [])

  const getGraphOrgData = useCallback((data) => {
    let arrStats = []
    data.user_stats.forEach((data) => {
      data.stats.forEach((st) => {
        arrStats.push(st)
      })
    })
    let objStats = {}
    arrStats.forEach(function (d) {
      if (Object.prototype.hasOwnProperty.call(objStats, d.date)) {
        objStats[d.date] = {
          ev_billable: objStats[d.date].ev_billable + d.ev_billable,
          ef_billable: objStats[d.date].ef_billable + d.ef_billable,
          prospect_billable: objStats[d.date].prospect_billable + d.prospect_billable,
          js_widget_billable: objStats[d.date].js_widget_billable + d.js_widget_billable,
          data_pulse_billable: objStats[d.date].data_pulse_billable + d.data_pulse_billable
        }
      } else {
        objStats[d.date] = {
          ev_billable: d.ev_billable,
          ef_billable: d.ef_billable,
          prospect_billable: d.prospect_billable,
          js_widget_billable: d.js_widget_billable,
          data_pulse_billable: d.data_pulse_billable
        }
      }
    })
    var userStatsOrg = [{ stats: [] }]
    for (var prop in objStats) {
      userStatsOrg[0].stats.push({
        date: prop,
        ev_billable: objStats[prop].ev_billable,
        ef_billable: objStats[prop].ef_billable,
        prospect_billable: objStats[prop].prospect_billable,
        js_widget_billable: objStats[prop].js_widget_billable,
        data_pulse_billable: objStats[prop].data_pulse_billable
      })
    }
    data.user_stats_org = userStatsOrg

    return getGraphData(data)
  }, [getGraphData])

  const setterMetrics = useCallback((stats, role) => {
    let globalTotalMembers = metrics.totalMembers
    let metric = { totalMembers: 0, totalCredits: 0, totalEVCredits: 0, totalEFCredits: 0, totalProspectCredits: 0 }
    let totalMembers = 0
    let totalEVCredits = 0
    let totalEFCredits = 0
    let totalProspectCredits = 0
    let totalJsWidgetCredits = 0
    let totalDPCredits = 0
    let totalCredits = 0
    let tableData = []

    if (role === 'all') {
      stats.forEach((data) => {
        totalMembers++
        let total_credits = 0
        let ev_billable = 0
        let ef_billable = 0
        let js_widget_billable = 0
        let prospect_billable = 0
        let data_pulse_billable = 0
        data.stats.forEach((stat) => {
          total_credits = total_credits + stat.total
          ev_billable = ev_billable + stat.ev_billable
          ef_billable = ef_billable + stat.ef_billable
          prospect_billable = prospect_billable + stat.prospect_billable
          js_widget_billable = js_widget_billable + stat.js_widget_billable
          data_pulse_billable = data_pulse_billable + stat.data_pulse_billable
        })
        tableData.push({
          total_credits: total_credits,
          ev_billable: ev_billable,
          ef_billable: ef_billable,
          prospect_billable: prospect_billable,
          js_widget_billable: js_widget_billable,
          data_pulse_billable: data_pulse_billable
        })
      })

      tableData.forEach((data) => {
        totalCredits = totalCredits + data.total_credits
        totalEVCredits = totalEVCredits + data.ev_billable
        totalEFCredits = totalEFCredits + data.ef_billable
        totalProspectCredits = totalProspectCredits + data.prospect_billable
        totalJsWidgetCredits = totalJsWidgetCredits + data.js_widget_billable
        totalDPCredits = totalDPCredits + data.data_pulse_billable
      })
    } else {
      if (stats[0].stats) {
        stats = stats[0].stats
      }
      stats.forEach((data) => {
        totalCredits = totalCredits + data.total
        totalEVCredits = totalEVCredits + data.ev_billable
        totalProspectCredits = totalProspectCredits + data.prospect_billable
        totalEFCredits = totalEFCredits + data.ef_billable
        totalJsWidgetCredits = totalJsWidgetCredits + data.js_widget_billable
        totalDPCredits = totalDPCredits + data.data_pulse_billable
      })
    }

    metric.totalMembers = globalTotalMembers >= totalMembers ? metrics.totalMembers : totalMembers
    metric.totalCredits = totalCredits
    metric.totalProspectCredits = totalProspectCredits
    metric.totalEVCredits = totalEVCredits
    metric.totalEFCredits = totalEFCredits
    metric.totalJsWidgetCredits = totalJsWidgetCredits
    metric.totalDPCredits = totalDPCredits

    return metric
  }, [metrics.totalMembers])

  const loadReportData = useCallback(() => {
    setIsGraphDataLoading(true)
    setIsStatsLoading(true)

    const { filters: apiFilters, selectedUserState } = buildApiFilters({
      viewedUser,
      memberFilter: filters.members,
      dateRangeFilter: filters.date_range
    })
    const filterMember = filters.members

    const queryParams = _.pick(
      { user_id: viewedUser.id, filters: JSON.stringify(apiFilters) },
      ApiHelper(client, 'OVER_ALL_USAGE_REPORTS').API_QUERY
    )

    ApiHelper(client, 'OVER_ALL_USAGE_REPORTS').API(client, queryParams)
      .then((res) => {
        setIsGraphDataLoading(false)
        if (res && res.status === Constants.CO_API.SUCCESS) {

          let usageStatsIndividualTable = selectedUserState.value.role === 'all' ?
            res.data.user_stats : res.data.user_stats[0].stats
          setUsageReports(res.data)

          setShowTable(selectedUserState.value.role === 'all' && filterMember.value.id == 1 ? 'orgAllData' : 'individual')
          setUsageStats(usageStatsIndividualTable)
          setIsStatsLoading(false)

          let collectGraphData = selectedUserState.value.role === 'all' && filterMember.value.id == 1
            ? getGraphOrgData(res.data) : getGraphData(res.data)

          setGraphData(collectGraphData)

          setMetrics(setterMetrics(usageStatsIndividualTable, selectedUserState.value.role))
        }
      })
      .catch((err) => {
        setIsGraphDataLoading(false)
        setIsStatsLoading(false)
        console.log('UsageReports api erred', err)
      })
  }, [client, viewedUser, filters, getGraphData, getGraphOrgData, setterMetrics])

  useEffect(() => {
    if (!_.isEmpty(filters)) {
      loadReportData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters])

  const refreshReports = () => {
    loadReportData()
  }

  const onSetFilters = (filters) => {
    setFiltersState({ ...filters })
  }

  return (
    <div className="analytics-graph-content">
      <div className="report-header col-12 d-flex justify-content-between align-items-center">
        <div className="report-date-filter">
          <Heading
            toolTipMessage="Find your credit utilization across all of your team members"
            title="Team Member Usage Reports"
          />
        </div>
      </div>
      <div className="report-header col-12 d-flex justify-content-between align-items-center">
        <AnalyticsFilters
          client={client}
          viewedUser={viewedUser}
          memberOptions={memberOptions}
          filterState={filters}
          onSetFilters={onSetFilters}
          urlMemberId={memberId}
        />
        <RefreshButton onRefresh={refreshReports} />
      </div>
      <div className="report-content ps-3 pe-3">
        <PaceLoader isLoading={isStatsLoading} />
        <div>
          <div className="d-flex report-performance-summary report-performance-summary-new g-0 flex-column report-team-summary pt-3">
            <div className="page-report-heading">
              <h3 className="m-0">Summary</h3>
              <div className="btn-toolbar">
                <div className="btn-group">
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => summaryToggle()}
                  >
                    {isPerfSummaryOpen === true ? (
                      <i className="fa fa-chevron-up"></i>
                    ) : (
                      <i className="fa fa-chevron-down"></i>
                    )}
                  </button>
                </div>
              </div>
            </div>
            <Collapse in={isPerfSummaryOpen}>
              <div className="graph-content">
                <div className="metrics">
                  <div className="metrics-content">
                    {metrics.totalMembers > 0 ? (
                      <div className="total-verified">
                        <span>Total Members</span>
                        {getMetrics(
                          metrics.totalMembers,
                          2,
                          '',
                          '',
                          'Total Members'
                        )}
                      </div>
                    ) : ''}
                    <div className="total-verified">
                      <span>Total Credits</span>
                      {getMetrics(
                        metrics.totalCredits,
                        2,
                        '',
                        '',
                        'Total Credits'
                      )}
                    </div>
                    <div className="total-verified">
                      <span>Email Verifier</span>
                      {getMetrics(
                        metrics.totalEVCredits,
                        2,
                        '',
                        '',
                        'Email Verifier'
                      )}
                    </div>
                    <div className="total-verified">
                      <span>Email Finder</span>
                      {getMetrics(
                        metrics.totalEFCredits,
                        2,
                        '',
                        '',
                        'Email Finder'
                      )}
                    </div>
                    <div className="total-verified">
                      <span>Prospect</span>
                      {getMetrics(
                        metrics.totalProspectCredits,
                        2,
                        '',
                        '',
                        'Prospect'
                      )}
                    </div>
                    <div className="total-verified">
                      <span>Form Guard</span>
                      {getMetrics(
                        metrics.totalJsWidgetCredits,
                        2,
                        '',
                        '',
                        'Form Guard'
                      )}
                    </div>
                    <div className="total-verified">
                      <span>Data Pulse</span>
                      {getMetrics(
                        metrics.totalDPCredits,
                        2,
                        '',
                        '',
                        'Data Pulse'
                      )}
                    </div>
                  </div>
                </div>
                <div className="area-graph">
                  <LineChart
                    graphData={graphData}
                    isGraphDataLoading={isGraphDataLoading}
                    width={300}
                    height={250}
                    position="bottom"
                  />
                </div>
              </div>
            </Collapse>
          </div>
        </div>
        <div>
          <div className="d-flex report-performance-breakdown g-0 flex-column">
            <div className="page-report-heading">
              <h3 className="m-0 d-flex align-items-center">
                {getTableHeading(isOrgAdminView(viewedUser) ? 'all' : viewedUser.user_role, usageReports.breakdown)}
              </h3>
            </div>
            {!isStatsLoading &&
              showTable === 'orgAllData' && filters.members?.value?.id == 1
              ? <ReportTableOrg
                userReportData={usageReports}
                tableData={usageStats}
              />
              : <ReportTableIndividual
                userReportData={usageReports}
                tableData={usageStats}
              />
            }
          </div>
        </div>
        <div className="report-footnote">
          <i
            className="fa fa-exclamation-circle"
            aria-hidden="true"
          >
          </i>
          <small className="form-text">
            Row will be supressed when no activity found for the particular
            day or hour for the chosen period
          </small>
        </div>
      </div>
    </div>
  )
}

export default Team
